import { CategoryIdDict, Dicts } from './types';
import { convertCategoryParametersToFlat, findArrValueIndex } from './utils';
import { ExtendedCategoryParamsDict, ExtentedCategoryParameters } from './types';

export type GeoInput = { countryId: string } | { regionId: string } | { cityId: string };

type CategoryInput = string[];

export type ParameterValue = {
  value: string[] | string | { from?: number; to?: number };
  id: string;
};

type Parameters = ParameterValue[];

export type CommonParams = {
  skip?: number;
  searchOnlyInTitle?: string;
  minPrice?: string;
  maxPrice?: string;
  sortBy?: string;
  search?: string;
  isFree?: boolean;
};

type InputParameters = {
  geo?: GeoInput;
  categories?: CategoryInput;
  parameters?: Parameters;
  commonParams: CommonParams;
};

type ParamsToPathDicts = Pick<Dicts, 'categoryIdDict'>;

const appendParamValue = (
  searchParams: URLSearchParams,
  key: string,
  value: string[] | string | { from?: number; to?: number },
) => {
  if (Array.isArray(value)) {
    value.forEach((item) => searchParams.append(key, String(item)));
    return;
  }
  if (typeof value === 'object') {
    Object.entries(value).forEach(([nestedKey, nestedValue]) => {
      if (nestedValue === undefined || nestedValue === null) {
        return;
      }

      searchParams.append(`${key}[${nestedKey}]`, String(nestedValue));
    });
    return;
  }

  searchParams.append(key, value);
};

export function isPureQueryUrl(url: string): boolean {
  const u = new URL(url, 'http://example.com');
  return u.pathname === '/' || u.pathname === '';
}

export function addPathFragment(url: string, path: string): string {
  const u = new URL(url, 'http://example.com');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  u.pathname = normalizedPath;

  return u.pathname + u.search + u.hash;
}

export function addFreeSegment(url: string): string {
  const u = new URL(url, 'http://example.com');
  const trimmedPathname = u.pathname === '/' ? '' : u.pathname.replace(/\/+$/, '');

  u.pathname = `${trimmedPathname}/free`;

  return u.pathname + u.search + u.hash;
}

export const convertParamsToPath = (
  input: InputParameters,
  dict: ParamsToPathDicts,
  geoElemName: string = '',
): string => {
  let url = geoElemName ? `/${geoElemName}` : '';

  const categoriesUrl = setCategories(input.categories || [], dict.categoryIdDict);
  url = url + categoriesUrl;
  const lastCategoryId = input.categories?.[(input.categories?.length || 1) - 1];
  const paramsUrl = setParameters(input.parameters || [], input.commonParams, lastCategoryId!, dict.categoryIdDict);

  url = url + paramsUrl;

  if (input.commonParams.isFree === true) {
    url = addFreeSegment(url);
  } else if (isPureQueryUrl(url)) {
    url = addPathFragment(url, '/search');
  }

  return url;
};

export const setCategories = (categories: CategoryInput, categoryIdDict: CategoryIdDict): string => {
  const categoriesNormalizedNames: string[] = [];

  categories.forEach((catId) => {
    const foundCategory = categoryIdDict[catId];
    if (!foundCategory) {
      throw new Error('Incorrect params: category');
    }
    categoriesNormalizedNames.push(foundCategory?.link);
  });

  return categoriesNormalizedNames.length > 0 ? `/${categoriesNormalizedNames.join('/')}` : '';
};

export const setParameters = (
  params: Parameters,
  commonParams: CommonParams,
  lastCategoryId: string,
  categoriesIdDict: CategoryIdDict,
) => {
  const foundCategory = categoriesIdDict[lastCategoryId || ''];

  const { flat: allCategoryParams, dict: allParamsDict } = convertCategoryParametersToFlat(
    foundCategory?.parameters || [],
  );
  validateDynamicParameters(allParamsDict, params);

  const paramsInUrl: string[] = [];

  const paramsArrayValue: ExtentedCategoryParameters[] = [];

  const inUrlParams =
    allCategoryParams.filter((catParam) => catParam.isUsedInUrl).sort((a, b) => a.order - b.order) || [];

  inUrlParams.forEach((catParam) => {
    const selectedParam = params.find((par) => par.id === catParam.id);

    //cannot be tis kind of params in url
    if (
      selectedParam?.value &&
      typeof selectedParam.value === 'object' &&
      ('from' in selectedParam.value || 'to' in selectedParam.value)
    ) {
      return;
    }
    if (!selectedParam) {
      return;
    }

    if (Array.isArray(selectedParam?.value)) {
      if (selectedParam.value.length === 0) {
        return;
      }
      if (selectedParam.value.length > 1) {
        paramsArrayValue.push(catParam);
        return;
      }

      selectedParam.value = selectedParam.value[0];
    }
    const selectedValueIndex = findArrValueIndex(catParam.enumOptions, selectedParam?.value as string);
    if (selectedValueIndex === -1) {
      throw new Error('Incorrect params: category params');
    }

    const strValue = catParam.enumSearchKeys?.[selectedValueIndex];
    if (strValue) {
      paramsInUrl.push(strValue);
    }
  });

  const getParams = new URLSearchParams();

  let inGetParams = allCategoryParams.filter((catParam) => !catParam.isUsedInUrl) || [];

  inGetParams = [...inGetParams, ...paramsArrayValue];

  inGetParams.forEach((catUrlParam) => {
    const selectedParam = params.find((par) => par.id === catUrlParam.id);

    if (!selectedParam || !selectedParam.value) {
      return;
    }

    appendParamValue(getParams, catUrlParam.normalizedName, selectedParam.value);
  });

  for (const key in commonParams) {
    const typedKey = key as keyof CommonParams;
    const val = commonParams[typedKey];
    if (typedKey === 'isFree') {
      continue;
    }
    if (typedKey === 'skip') {
      continue;
    }
    if ((typedKey === 'minPrice' || typedKey === 'maxPrice') && commonParams.isFree === true) {
      continue;
    }
    if (val !== undefined && val !== null) {
      getParams.append(typedKey, String(val));
    }
  }
  const inurlsParamsString = paramsInUrl.length > 0 ? `/${paramsInUrl.join('/')}` : '';
  const queryString = getParams.toString();
  return queryString ? `${inurlsParamsString}?${queryString}` : inurlsParamsString;
};

const validateDynamicParameters = (allParamsDict: ExtendedCategoryParamsDict, selectedParams: Parameters) => {
  selectedParams.forEach(({ id, value }) => {
    const param: ExtentedCategoryParameters = allParamsDict[id];
    if (param.parentId && 'dynamicVariant' in param) {
      // to do validate

      const parentParamInSelected = selectedParams.find(({ id }) => id === param.parentId);

      let value = parentParamInSelected?.value;

      if (Array.isArray(value)) {
        if (value.length !== 1) {
          throw new Error('Incorrect params: category params');
        }

        value = value[0];

        if (param.dynamicVariant !== value) {
          throw new Error('Incorrect params: category params');
        }
      }
    }
  });
};
